import {useEffect, useMemo, useState} from 'react';

import {CssBaseline, ThemeProvider} from '@mui/material';

import './App.css';
import ContentGenerator from './ContentGenerator';
import ContentReview from './ContentReview';
import {WorkspaceHeader} from './contentGenerator/components/ui';
import AppAuthGate from './shared/AppAuthGate';
import ErrorBoundary from './shared/ErrorBoundary';
import {ProductProvider} from './shared/ProductContext';
import BrandSetupWizard from './shared/brand/BrandSetupWizard';
import {loadBrandProfile} from './shared/brand/brandService';
import UserManagement from './shared/users/UserManagement';
import {trackUsageEvent} from './shared/usageStats';
import {applyThemeVars, createAppTheme} from './theme';

const WORKSPACE_STORAGE_KEY = 'cg-workspace';
const THEME_STORAGE_KEY = 'cg-theme';
const API_KEY_STORAGE_KEY = 'cg-api-key';

const getStoredValue = (key, fallback) => {
    if (typeof window === 'undefined') return fallback;
    return window.localStorage.getItem(key) || fallback;
};

function AuthedApp({theme, setTheme, currentUser, onLogout}) {
    const [workspace, setWorkspace] = useState(() => getStoredValue(WORKSPACE_STORAGE_KEY, 'generator'));
    const [apiKey, setApiKey] = useState('');

    const [brandProfile, setBrandProfile] = useState(null);
    const [brandProfileLoaded, setBrandProfileLoaded] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(WORKSPACE_STORAGE_KEY, workspace);
        trackUsageEvent('workspace.view', {workspace});
    }, [workspace]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.removeItem(API_KEY_STORAGE_KEY);
    }, []);

    useEffect(() => {
        let cancelled = false;

        loadBrandProfile()
            .then((profile) => {
                if (cancelled) return;
                setBrandProfile(profile?.companyName ? profile : null);
            })
            .catch(() => {
                if (cancelled) return;
                setBrandProfile(null);
            })
            .finally(() => {
                if (cancelled) return;
                setBrandProfileLoaded(true);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    if (!brandProfileLoaded) {
        return (
            <div className="App">
                <div className="app-shell__content">Loading workspace...</div>
            </div>
        );
    }

    if (!brandProfile) {
        return (
            <div className="App">
                <BrandSetupWizard
                    apiKey={apiKey}
                    onComplete={(profile) => setBrandProfile(profile)}
                />
            </div>
        );
    }

    return (
        <div className="App">
            <div className="app-shell">
                <WorkspaceHeader
                    workspace={workspace}
                    onWorkspaceChange={setWorkspace}
                    apiKey={apiKey}
                    onApiKeyChange={setApiKey}
                    theme={theme}
                    toggleTheme={() =>
                        setTheme((currentTheme) => (currentTheme === 'dark' ? 'light' : 'dark'))
                    }
                    brandProfile={brandProfile}
                    onBrandProfileChange={setBrandProfile}
                    currentUser={currentUser}
                    onLogout={onLogout}
                />
                <div className="app-shell__content">
                    {workspace === 'users' ? (
                        <ErrorBoundary>
                            <UserManagement currentUser={currentUser} />
                        </ErrorBoundary>
                    ) : (
                        <ProductProvider>
                            <div
                                style={{
                                    display: workspace === 'review' ? 'block' : 'none',
                                    width: '100%',
                                    minHeight: 0,
                                }}>
                                <ErrorBoundary>
                                    <ContentReview
                                        apiKey={apiKey}
                                        brandProfile={brandProfile}
                                        theme={theme}
                                        isActive={workspace === 'review'}
                                    />
                                </ErrorBoundary>
                            </div>
                            <div
                                style={{
                                    display: workspace === 'generator' ? 'block' : 'none',
                                    width: '100%',
                                    minHeight: 0,
                                    overflow: 'auto',
                                }}>
                                <ErrorBoundary>
                                    <ContentGenerator
                                        apiKey={apiKey}
                                        brandProfile={brandProfile}
                                        theme={theme}
                                        onWorkspaceChange={setWorkspace}
                                    />
                                </ErrorBoundary>
                            </div>
                        </ProductProvider>
                    )}
                </div>
            </div>
        </div>
    );
}

function App() {
    const [theme, setTheme] = useState(() => getStoredValue(THEME_STORAGE_KEY, 'light'));
    const muiTheme = useMemo(() => createAppTheme(theme), [theme]);

    useEffect(() => {
        applyThemeVars(theme);
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }, [theme]);

    return (
        <ThemeProvider theme={muiTheme}>
            <CssBaseline />
            <AppAuthGate>
                {({user, onLogout}) => (
                    <AuthedApp
                        theme={theme}
                        setTheme={setTheme}
                        currentUser={user}
                        onLogout={onLogout}
                    />
                )}
            </AppAuthGate>
        </ThemeProvider>
    );
}

export default App;
